import { MprcResolveRequestErrors } from 'components/MprcRequestErrorReport';
import { ChannelRoles, DeleteMemberPayload, MemberType } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseChannelMemberOperationsReturn {
    addMembers: (params: AddMembersParams) => Promise<boolean>;
    deleteMember: (params: DeleteMemberPayload) => Promise<boolean>;
    changeMemberRole: (params: ChangeMemberRoleParams) => Promise<boolean>;
    loading: boolean;
    error: any;
    clearError: () => void;
    setError: (e: any) => void;
}

interface AddMembersParams {
    channelId: string;
    memberType: MemberType;
    members: string[];
    role: ChannelRoles;
}

interface ChangeMemberRoleParams {
    channelId: string;
    memberId: string;
    memberType: MemberType;
    role: ChannelRoles;
}

export const useChannelMemberOperations = (): UseChannelMemberOperationsReturn => {
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(null);

    const clearError = () => setError(null);

    const addMembers = async (params: AddMembersParams): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const result = await api.v1.channelManagement.addMembers(params);
            const affectedCount = result.data;

            if (affectedCount > 0) {
                enqueueSnackbar(
                    t("Channel.Edit.Members.AddMemberSuccessMessage", { count: affectedCount }),
                    { variant: "success" }
                );
                return true;
            } else {
                enqueueSnackbar(
                    t("Channel.Edit.Members.AddMemberFailMessage"),
                    { variant: "error" }
                );
                return false;
            }
        } catch (e) {
            const r = MprcResolveRequestErrors(e);
            if (r.fieldsWithError?.["members"]) {
                r.fieldsWithError["members"].forEach(element => {
                    enqueueSnackbar(element, { variant: "error" });
                });
            } else {
                setError(e);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const deleteMember = async (params: DeleteMemberPayload): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const result = await api.v1.channelManagement.deleteMember(params);

            if (result.data === true) {
                enqueueSnackbar(
                    t("Channel.Edit.Members.DeleteMemberSuccessMessage"),
                    { variant: "success" }
                );
                return true;
            } else {
                enqueueSnackbar(
                    t("Channel.Edit.Members.DeleteMemberFailMessage"),
                    { variant: "error" }
                );
                return false;
            }
        } catch (e) {
            const r = MprcResolveRequestErrors(e);
            if (r.fieldsWithError) {
                const memberErrors = r.fieldsWithError["memberId"] ?? [];
                const channelErrors = r.fieldsWithError["channelId"] ?? [];
                [...memberErrors, ...channelErrors].forEach(item => {
                    enqueueSnackbar(item, { variant: "error" });
                });
            } else {
                setError(e);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const changeMemberRole = async (params: ChangeMemberRoleParams): Promise<boolean> => {
        setLoading(true);
        setError(null);

        try {
            const result = await api.v1.channelManagement.changeMemberRole(params);

            if (result.data === true) {
                enqueueSnackbar(
                    t("Channel.Edit.Members.ChangeMemberRoleSuccessMessage"),
                    { variant: "success" }
                );
                return true;
            } else {
                enqueueSnackbar(
                    t("Channel.Edit.Members.ChangeMemberRoleFailMessage"),
                    { variant: "error" }
                );
                return false;
            }
        } catch (e) {
            setError(e);
            return false;
        } finally {
            setLoading(false);
        }
    };

    return {
        addMembers,
        deleteMember,
        changeMemberRole,
        loading,
        error,
        setError,
        clearError
    };
};
